package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.duoma.duomaproducao.ui.misc.isCompactScreen
import br.com.duoma.duomaproducao.ui.misc.mediaQuery
import br.com.duoma.duomaproducao.viewmodel.LineChartManager
import com.patrykandpatrick.vico.compose.cartesian.CartesianChartHost
import com.patrykandpatrick.vico.compose.cartesian.Scroll
import com.patrykandpatrick.vico.compose.cartesian.Zoom
import com.patrykandpatrick.vico.compose.cartesian.axis.HorizontalAxis
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianValueFormatter
import com.patrykandpatrick.vico.compose.cartesian.axis.VerticalAxis
import com.patrykandpatrick.vico.compose.cartesian.axis.rememberAxisGuidelineComponent
import com.patrykandpatrick.vico.compose.cartesian.axis.rememberAxisLineComponent
import com.patrykandpatrick.vico.compose.cartesian.layer.LineCartesianLayer
import com.patrykandpatrick.vico.compose.cartesian.layer.rememberLineCartesianLayer
import com.patrykandpatrick.vico.compose.cartesian.marker.CartesianMarkerController
import com.patrykandpatrick.vico.compose.cartesian.marker.DefaultCartesianMarker
import com.patrykandpatrick.vico.compose.cartesian.marker.LineCartesianLayerMarkerTarget
import com.patrykandpatrick.vico.compose.cartesian.marker.rememberDefaultCartesianMarker
import com.patrykandpatrick.vico.compose.cartesian.rememberCartesianChart
import com.patrykandpatrick.vico.compose.cartesian.rememberVicoScrollState
import com.patrykandpatrick.vico.compose.cartesian.rememberVicoZoomState
import com.patrykandpatrick.vico.compose.common.Fill
import com.patrykandpatrick.vico.compose.common.component.LineComponent
import com.patrykandpatrick.vico.compose.common.component.ShapeComponent
import com.patrykandpatrick.vico.compose.common.component.rememberTextComponent

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun <T> WorkCenterLineChart(
    lineChartManager: LineChartManager<T>,
    modifier: Modifier = Modifier
) {
    val colorScheme = MaterialTheme.colorScheme
    val typography = MaterialTheme.typography
    val lines = remember(lineChartManager.legends) {
        lineChartManager.legends.ifEmpty { listOf("Carregando.." to Color.Red) }
            .map { LineCartesianLayer.Line(LineCartesianLayer.LineFill.single(Fill(it.second))) }
    }
    @OptIn(ExperimentalComposeUiApi::class)
    Card(
        modifier = modifier,
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = colorScheme.surface),
        shape = RoundedCornerShape(12.dp),
    ) {
        Column(Modifier.fillMaxWidth().padding(top = 16.dp, start = 16.dp, end = 16.dp, bottom = 16.dp)) {
            // ── Legend & Filter ──────────────────────────────────────────
            val hasFilter = lineChartManager.onToggleFilter != null || lineChartManager.reorderable
            if (mediaQuery(false, true)) {
                // Mobile: filter centered on top, legends centered below
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    if (hasFilter) {
                        Filter(
                            lineChartManager.filterList,
                            lineChartManager.onMove,
                            lineChartManager.onToggleFilter ?: {})
                        Spacer(Modifier.height(8.dp))
                    }
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterHorizontally),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        lineChartManager.legends.forEachIndexed { i, l ->
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(Modifier.size(8.dp).clip(CircleShape).background(lineChartManager.legends[i].second))
                                Spacer(Modifier.width(4.dp))
                                Text(text = l.first, style = typography.labelSmall, color = colorScheme.onSurfaceVariant)
                            }
                        }
                    }
                }
            } else {
                // Desktop: legends left, filter right
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.Top,
                ) {
                    FlowRow(
                        modifier = Modifier.weight(1f),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        lineChartManager.legends.forEachIndexed { i, l ->
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(Modifier.size(8.dp).clip(CircleShape).background(lineChartManager.legends[i].second))
                                Spacer(Modifier.width(4.dp))
                                Text(text = l.first, style = typography.labelSmall, color = colorScheme.onSurfaceVariant)
                            }
                        }
                    }
                    if (hasFilter) {
                        Spacer(Modifier.width(8.dp))
                        Filter(
                            lineChartManager.filterList,
                            lineChartManager.onMove,
                            lineChartManager.onToggleFilter ?: {})
                    }
                }
            }

            Spacer(Modifier.height(12.dp))

            // ── Chart ────────────────────────────────────────────────────
            val marker = rememberDefaultCartesianMarker(
                label = rememberTextComponent(
                    typography.labelSmall.copy(
                        fontWeight = FontWeight.SemiBold,
                        color = colorScheme.onSurface,
                    ),
                    lineCount = 8,
                    background = ShapeComponent(Fill(Color.White), RoundedCornerShape(4.dp))
                ),
                valueFormatter = { _, targets ->
                    targets.filterIsInstance<LineCartesianLayerMarkerTarget>()
                        .firstOrNull()
                        ?.points
                        ?.mapIndexedNotNull { _, point ->
                            val qty = point.entry.y.toInt()
                            if (qty == 0) null
                            else "${lineChartManager.legends.first { it.second == point.color }.first}: $qty pçs"
                        }
                        ?.joinToString("\n")
                        ?: "-"
                },
                labelPosition = DefaultCartesianMarker.LabelPosition.AroundPoint,
                indicator = { ShapeComponent(Fill(it), CircleShape) },
                indicatorSize = 6.dp,
                guideline = LineComponent(Fill(colorScheme.outlineVariant), 1.dp),
            )
            CartesianChartHost(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(260.dp)
                    .padding(end = 8.dp),
                scrollState = rememberVicoScrollState(
                    scrollEnabled = true,
                    initialScroll = Scroll.Absolute.End,
                ),
                zoomState = rememberVicoZoomState(zoomEnabled = isCompactScreen(), ),
                chart = rememberCartesianChart(
                    rememberLineCartesianLayer(
                        lineProvider = LineCartesianLayer.LineProvider.series(lines),
                    ),
                    startAxis = VerticalAxis.rememberStart(
                        label = rememberTextComponent(
                            typography.labelSmall.copy(color = colorScheme.onSurfaceVariant),
                        ),
                        valueFormatter = CartesianValueFormatter { _, value, _ ->
                            try { value.toLong().toString() } catch (_: Exception) { "0" }
                        },
                        guideline = rememberAxisGuidelineComponent(Fill(Color.Black.copy(alpha = 0.4f)), 1.dp),
                        line = rememberAxisLineComponent(Fill(colorScheme.outline), 1.dp),
                    ),
                    bottomAxis = HorizontalAxis.rememberBottom(
                        valueFormatter = remember(lineChartManager) {
                            CartesianValueFormatter { ctx, v, cv ->
                                lineChartManager.bottomLabelFormatter.format(ctx, v, cv).ifBlank { "-" }
                            }
                        },
                        guideline = null,
                        label = rememberTextComponent(
                            typography.labelSmall.copy(
                                fontWeight = FontWeight.SemiBold,
                                color = colorScheme.onSurface,
                                textAlign = TextAlign.Center,
                                lineHeight = 12.sp
                            ),
                            lineCount = 2
                        ),
                        itemPlacer = remember { HorizontalAxis.ItemPlacer.aligned() },
                        line = rememberAxisLineComponent(Fill(colorScheme.outline), 1.dp),
                    ),
                    marker = marker,
                    markerController = CartesianMarkerController.rememberShowOnHover()
                ),
                modelProducer = lineChartManager.modelProducer,
                placeholder = { Box(Modifier.fillMaxSize()) },
            )
        }
    }
}